import { FC, useState, useMemo, useCallback } from "react";
import { useBaseWorkspacesDetailsQuery } from "@api/workspace.api";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useScope } from "@state/scopeContext.lib";
import { WorkspaceSortValue } from "@common/types/views/workspaceViews";
import { StickyTableFooter } from "@design-system/StickyTableFooter/StickyTableFooter";
import stickyFooterStyles from "@design-system/StickyTableFooter/StickyTableFooter.module.scss";
import { Pagination } from "@design-system/Pagination/Pagination";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { pluralize } from "@common/helpers/string";
import { DefaultWorkspaceView } from "./views/DefaultWorkspaceView";
import {
    workspaceViewConfig,
    WorkspaceDetailsRow,
} from "./views/workspaceViewConfig";
import styles from "./Workspaces.module.scss";

interface WorkspacesViewProps {
    search?: string;
    setSearch?: (search: string) => void;
}

const PAGE_SIZE = 20;

export const WorkspacesView: FC<WorkspacesViewProps> = ({
    search,
    setSearch,
}) => {
    const [page, setPage] = useState(1);
    const { changeActiveWorkspace } = useScope();
    const [currentSort, setCurrentSort] = useState<WorkspaceSortValue>(
        WorkspaceSortValue.NAME_ASC,
    );

    const changeSort = useCallback(
        (sort: WorkspaceSortValue) => {
            setCurrentSort(sort);
            setPage(1);
        },
        [setCurrentSort, setPage],
    );

    const params = useMemo(
        () => ({
            limit: PAGE_SIZE,
            page: page - 1, // Backend expects zero-based page
            filters: { search },
            sort: currentSort,
            excludeUserOrganization: true,
        }),
        [page, currentSort, search],
    );

    const { data: workspaceResponse } = useBaseWorkspacesDetailsQuery(params);
    const totalPages = workspaceResponse?.pageCount ?? 1;

    const rows: WorkspaceDetailsRow[] | null = useMemo(() => {
        if (!workspaceResponse) {
            return null;
        }
        return workspaceResponse.data.map((w) => ({
            ...w,
            id: w.workspace.id,
        }));
    }, [workspaceResponse]);

    const handleRowClick = useCallback(
        (row?: WorkspaceDetailsRow) => {
            if (row) {
                changeActiveWorkspace(row.workspace.id);
            }
        },
        [changeActiveWorkspace],
    );

    return (
        <ViewTable
            data={rows}
            view={DefaultWorkspaceView}
            config={workspaceViewConfig}
            sort={currentSort}
            onSortChange={changeSort}
            onRowClick={handleRowClick}
            className={styles.workspacesView}
            fixedTopOffsetPx
            emptyView={() => (
                <EmptyState
                    header="No workspaces found"
                    action={() => setSearch?.("")}
                />
            )}
            footer={
                workspaceResponse?.total ? (
                    <StickyTableFooter
                        borderless
                        className={styles.stickyFooter}
                    >
                        <div className={stickyFooterStyles.row}>
                            <div className={stickyFooterStyles.rowContent}>
                                <span>
                                    {workspaceResponse?.total}{" "}
                                    {pluralize(
                                        "workspace",
                                        workspaceResponse?.total ?? 0,
                                    )}
                                </span>

                                <Pagination
                                    pageCount={totalPages}
                                    currentPage={page}
                                    onPageChange={setPage}
                                />
                            </div>
                        </div>
                    </StickyTableFooter>
                ) : null
            }
        />
    );
};
